import { ActionStatus, Room } from '../../../Constants';
import {
    debrisOnStation,
    isThereACoreRoomInStorage,
    upgradedAmenitiesAndBedsInStorage,
    upgradedCoreRoomInStorage
} from '../../Game/GamePlayerInfo';
import { isRoomActivated, removableRooms } from '../../TwoD/Common/StationUtilities';


function install(gamePlayerController, aiPackage, stationIndex, storageIndex) {
    if (aiPackage.execute && storageIndex !== null) {
        gamePlayerController.gamePlayerApi.installFromStorage(stationIndex, storageIndex);
    } else if (aiPackage.execute) {
        gamePlayerController.gamePlayerApi.clickNext();
    }
}

function uninstall(gamePlayerController, aiPackage, stationIndex) {
    if (aiPackage.execute && stationIndex !== null) {
        gamePlayerController.gamePlayerApi.uninstallToStorage(stationIndex);
    } else if (aiPackage.execute) {
        gamePlayerController.gamePlayerApi.clickNext();
    }
}


function coreRooms(gamePlayerController, aiPackage) {
    let question = 'Is there an upgraded Core Room in storage?';
    let result = false;
    let reasoning = 'Get core rooms re-installed as soon as possible.';
    let storageIndex = upgradedCoreRoomInStorage(gamePlayerController.gamePlayerData);
    let stationIndex = null;
    if (storageIndex != null) {
        const roomId = gamePlayerController.gamePlayerData.storage[storageIndex];
        if (roomId) {
            stationIndex = gamePlayerController.gamePlayerData.station.indexOf(
                Room.RANDOM_ACTION_ROOM_ID);
            // stationIndex === -1 means there is no emptied core-room slot (no
            // RANDOM_ACTION placeholder) to re-install into. Without this
            // stationIndex >= 0 guard install() fires installFromStorage(-1, ..)
            // and the engine reads destination=-1 as a negative Python index
            // (the LAST station slot). Leave success false so aiRenovate falls
            // through to extraRooms/debris/coreRoom -> clickNext.
            if (stationIndex >= 0 &&
                    !gamePlayerController.gamePlayerData.locked_rooms.includes(stationIndex)) {
                result = true;
                aiPackage.addToMessage(`Re-Install ${Room.NAMES[roomId]}`);
                aiPackage.success = true;
            }
        }
    }
    aiPackage.addProvenance(question, result, reasoning);
    return { stationIndex:stationIndex, storageIndex:storageIndex };
}

function extraRooms(gamePlayerController, aiPackage) {
    const question = 'Are there any upgraded Beds or Amenities to install?'
    const storageIndex = upgradedAmenitiesAndBedsInStorage(gamePlayerController.gamePlayerData);
    let stationIndex = null;
    let result = false;
    const reasoning = 'Install Beds and Amenities into empty spaces.'
    if (storageIndex != null) {
        const roomId = gamePlayerController.gamePlayerData.storage[storageIndex];
        stationIndex = gamePlayerController.gamePlayerData.station.indexOf(null);
        if (stationIndex >= 0) {
            result = true;
            aiPackage.addToMessage(`Install ${Room.NAMES[roomId]}`);
            aiPackage.success = true;
        }

    }
    aiPackage.addProvenance(question, result, reasoning);
    return { stationIndex:stationIndex, storageIndex:storageIndex };
}

function debris(gamePlayerController, aiPackage) {
    const question = 'Is there debris?'
    const stationIndex = debrisOnStation(gamePlayerController.gamePlayerData);
    let result = false;
    const reasoning = 'Debris is worth Snohe if Uninstalled.'
    // Only select an index the engine will accept — removable_rooms excludes
    // Built-In and locked_rooms indexes. Picking a locked tile drew the
    // uninstall_not_removable Reject (G147, 2026-07-14). removableRooms() is
    // the same predicate the backend enforces (GamePlayer.removable_rooms).
    const removable = removableRooms(gamePlayerController.gamePlayerData);
    if (stationIndex != null && removable.includes(stationIndex)) {
            result = true;
            aiPackage.addToMessage(`Uninstall Debris`);
            aiPackage.success = true;
        }
    aiPackage.addProvenance(question, result, reasoning);
    return stationIndex;
}

function coreRoom(gamePlayerController, aiPackage) {
    let question = 'Is there an upgraded Core Room in storage?';
    let result = isThereACoreRoomInStorage(gamePlayerController.gamePlayerData);
    let reasoning = 'I never uninstall more than 1 Core room at a time.';
    aiPackage.addProvenance(question, result, reasoning);
    if (result) return null;
    // # advertisement, robot, lodging, renovation, ghost, research
    const upgrade_order = [6, 4, 10, 8, 12, 2];
    // Only uninstall a station index the engine will accept. The backend
    // rejects any index not in GamePlayer.removable_rooms() (roomId set, not a
    // Built-In room, not in locked_rooms) with uninstall_not_removable. The
    // old gate used only !isRoomActivated (a robot-activation check, NOT a
    // removability check), so a non-activated core room at a LOCKED station
    // was selected and Rejected (G147, 2026-07-14). Pair removability (the
    // human-UI predicate removableRooms) WITH the not-activated preference.
    const removable = removableRooms(gamePlayerController.gamePlayerData);
    let stationIndex = null;
    for (const targetRoomId of upgrade_order) {
        const candidate = gamePlayerController.gamePlayerData.station.indexOf(
            targetRoomId);
        if (candidate >= 0 &&
                removable.includes(candidate) &&
                !isRoomActivated(gamePlayerController.gamePlayerData, candidate)) {
            stationIndex = candidate;
            aiPackage.addToMessage(`Uninstall Core Room ${Room.NAMES[targetRoomId]}`);
            aiPackage.success = true;
            break;
        }
    }
    return stationIndex;
}

export function aiRenovate(gamePlayerController, aiPackage) {
    let result = coreRooms(gamePlayerController, aiPackage);
    if (!aiPackage.success) {
        result = extraRooms(gamePlayerController, aiPackage);
    }
    if (aiPackage.success) {
        install(gamePlayerController, aiPackage,
            result.stationIndex, result.storageIndex);
        return;
    }

    result = debris(gamePlayerController, aiPackage);
    if (!aiPackage.success) {
        result = coreRoom(gamePlayerController, aiPackage);
    }
    if (aiPackage.success) {
        uninstall(gamePlayerController, aiPackage, result)
        return;
    }

    // todo: renovation sell.

    aiPackage.success = false;
    aiPackage.addToMessage(`I don't want to Renovate`);
    if (aiPackage.execute) {
        gamePlayerController.gamePlayerApi.clickNext();
    }
}

export default function artificialRenovationAction(gamePlayerController, aiPackage) {
    switch (gamePlayerController.gamePlayerData.action_status) {
    case ActionStatus.INIT_ID:
        aiPackage.addToMessage('Renovation Initializing');
        return aiPackage;
    case ActionStatus.WAIT_ID:
        return aiRenovate(gamePlayerController, aiPackage);
    default:
        break;
    }
}
